const normalizeText = (value: any) => String(value || "").trim();

const formatProtocolParameterLabel = (key: string) => (
  String(key || "")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/^\w/, (character) => character.toUpperCase())
);
const normalizeKey = (value: any) => normalizeText(value).toLowerCase();

export const EMAR_PROTOCOL_CATEGORY_OPTIONS = [
  {
    label: "Blood Glucose",
    value: "sliding_scale_insulin",
    guidanceAction: "Calculate dose",
    requiresDoseInput: true,
    parameterSchema: [
      { key: "bloodGlucose", label: "Blood Glucose", unit: "mg/dL", inputType: "number", required: true },
    ],
  },
  {
    label: "Correctional Insulin",
    value: "correctional_insulin",
    guidanceAction: "Calculate dose",
    requiresDoseInput: true,
    parameterSchema: [
      { key: "bloodGlucose", label: "Blood Glucose", unit: "mg/dL", inputType: "number", required: true },
    ],
  },
  {
    label: "Blood Pressure",
    value: "bp_medication_hold",
    guidanceAction: "Give / Hold",
    requiresDoseInput: false,
    parameterSchema: [
      { key: "systolicBp", label: "Systolic BP", unit: "mmHg", inputType: "number", required: true },
      { key: "diastolicBp", label: "Diastolic BP", unit: "mmHg", inputType: "number", required: true },
    ],
  },
  {
    label: "Pulse",
    value: "heart_rate_hold",
    guidanceAction: "Give / Hold",
    requiresDoseInput: false,
    parameterSchema: [
      { key: "pulse", label: "Pulse", unit: "bpm", inputType: "number", required: true },
    ],
  },
  {
    label: "BP + Pulse Hold",
    value: "bp_pulse_hold",
    guidanceAction: "Give / Hold",
    requiresDoseInput: false,
    parameterSchema: [
      { key: "systolicBp", label: "Systolic BP", unit: "mmHg", inputType: "number", required: true },
      { key: "diastolicBp", label: "Diastolic BP", unit: "mmHg", inputType: "number", required: true },
      { key: "pulse", label: "Pulse", unit: "bpm", inputType: "number", required: true },
    ],
  },
  {
    label: "Oxygen / SpO2",
    value: "oxygen_therapy",
    guidanceAction: "Give / Hold / Adjust O2",
    requiresDoseInput: false,
    parameterSchema: [
      { key: "spo2", label: "SpO2", unit: "%", inputType: "number", required: true },
    ],
  },
  {
    label: "PRN Pain Medication",
    value: "prn_pain_medication",
    guidanceAction: "Give / Do Not Give",
    requiresDoseInput: false,
    parameterSchema: [
      { key: "painScore", label: "Pain Score", unit: "/10", inputType: "number", required: true },
    ],
  },
  {
    label: "Pain Score",
    value: "pain_score",
    guidanceAction: "Assess",
    requiresDoseInput: false,
    parameterSchema: [
      { key: "painScore", label: "Pain Score", unit: "/10", inputType: "number", required: true },
    ],
  },
  {
    label: "Pain Assessment",
    value: "pain_assessment",
    guidanceAction: "Assess",
    requiresDoseInput: false,
    parameterSchema: [
      {
        key: "painAssessment",
        label: "Pain Assessment",
        unit: "",
        inputType: "select",
        options: ["Mild", "Moderate", "Severe"],
        required: true,
      },
    ],
  },
  {
    label: "PRN Anxiety Medication",
    value: "prn_anxiety_medication",
    guidanceAction: "Give / Do Not Give",
    requiresDoseInput: false,
    parameterSchema: [
      { key: "anxietyScore", label: "Anxiety Score", unit: "", inputType: "number", required: true },
    ],
  },
  {
    label: "PRN Sleep Medication",
    value: "prn_sleep_medication",
    guidanceAction: "Give / Do Not Give",
    requiresDoseInput: false,
    parameterSchema: [
      { key: "sleepAssessment", label: "Sleep Assessment", unit: "", inputType: "text", required: true },
    ],
  },
  {
    label: "PRN Constipation Medication",
    value: "prn_constipation_medication",
    guidanceAction: "Give / Do Not Give",
    requiresDoseInput: false,
    parameterSchema: [
      { key: "daysSinceBm", label: "Days Since BM", unit: "days", inputType: "number", required: true },
    ],
  },
  {
    label: "PRN Diarrhea Medication",
    value: "prn_diarrhea_medication",
    guidanceAction: "Give / Do Not Give",
    requiresDoseInput: false,
    parameterSchema: [
      { key: "stoolCount", label: "Stool Count", unit: "", inputType: "number", required: true },
    ],
  },
  {
    label: "Temperature",
    value: "fever_medication",
    guidanceAction: "Give / Do Not Give",
    requiresDoseInput: false,
    parameterSchema: [
      { key: "temperature", label: "Temperature", unit: "F", inputType: "number", required: true },
    ],
  },
  {
    label: "Weight",
    value: "weight_based_medication",
    guidanceAction: "Calculate dose",
    requiresDoseInput: true,
    parameterSchema: [
      { key: "weight", label: "Weight", unit: "lb", inputType: "number", required: true },
    ],
  },
  {
    label: "INR-Based Medication",
    value: "inr_based_medication",
    guidanceAction: "Dose / Hold",
    requiresDoseInput: true,
    parameterSchema: [
      { key: "inrValue", label: "INR Value", unit: "", inputType: "number", required: true },
    ],
  },
  {
    label: "Lab-Based Medication",
    value: "lab_based_medication",
    guidanceAction: "Dose / Hold",
    requiresDoseInput: true,
    parameterSchema: [
      { key: "labValue", label: "Lab Value", unit: "", inputType: "number", required: true },
    ],
  },
  {
    label: "Fluid Restriction Monitoring",
    value: "fluid_restriction_monitoring",
    guidanceAction: "Alert",
    requiresDoseInput: false,
    parameterSchema: [
      { key: "dailyIntake", label: "Daily Intake", unit: "mL", inputType: "number", required: true },
    ],
  },
  {
    label: "Seizure Rescue Medication",
    value: "seizure_rescue_medication",
    guidanceAction: "Give / Call 911",
    requiresDoseInput: false,
    parameterSchema: [
      { key: "seizureDuration", label: "Seizure Duration", unit: "minutes", inputType: "number", required: true },
    ],
  },
  {
    label: "Respiratory Treatment",
    value: "respiratory_treatment",
    guidanceAction: "Give / Hold",
    requiresDoseInput: false,
    parameterSchema: [
      { key: "respiratoryRate", label: "Respiratory Rate", unit: "breaths/min", inputType: "number", required: true },
      { key: "spo2", label: "SpO2", unit: "%", inputType: "number", required: true },
    ],
  },
  {
    label: "Respiration",
    value: "respiration_monitoring",
    guidanceAction: "Alert",
    requiresDoseInput: false,
    parameterSchema: [
      { key: "respiratoryRate", label: "Respiratory Rate", unit: "breaths/min", inputType: "number", required: true },
    ],
  },
  {
    label: "Behavioral PRN Medication",
    value: "behavioral_prn_medication",
    guidanceAction: "Give / Do Not Give",
    requiresDoseInput: false,
    parameterSchema: [
      { key: "behaviorSeverity", label: "Behavior Severity", unit: "", inputType: "text", required: true },
    ],
  },
  {
    label: "Nausea Medication",
    value: "nausea_medication",
    guidanceAction: "Give / Do Not Give",
    requiresDoseInput: false,
    parameterSchema: [
      { key: "nauseaSeverity", label: "Nausea Severity", unit: "", inputType: "text", required: true },
    ],
  },
  {
    label: "Custom",
    value: "custom_protocol",
    guidanceAction: "Custom Action",
    requiresDoseInput: false,
    parameterSchema: [
      { key: "customAssessment", label: "Assessment", unit: "", inputType: "text", required: true },
    ],
  },
];

const EMAR_PROTOCOL_EDITOR_VISIBLE_VALUES = new Set([
  "sliding_scale_insulin",
  "bp_medication_hold",
  "heart_rate_hold",
  "oxygen_therapy",
  "fever_medication",
  "weight_based_medication",
  "pain_score",
  "pain_assessment",
]);

export const getEmarProtocolParameterSchema = (category: any, rules?: any[]) => {
  const categoryOption = getEmarProtocolCategoryOption(category);
  const parameterSchema = Array.isArray(categoryOption?.parameterSchema)
    ? (categoryOption?.parameterSchema || [])
    : [];
  return parameterSchema.map((field: any) => {
    const listCondition = (Array.isArray(rules) ? rules : [])
      .map((rule: any) => rule?.conditions?.[String(field?.key || "").trim()])
      .find((condition: any) => String(condition?.operator || "").trim() === "list" && String(condition?.equalsValue || "").trim());
    const requiredCondition = (Array.isArray(rules) ? rules : [])
      .map((rule: any) => rule?.conditions?.[String(field?.key || "").trim()])
      .some((condition: any) => String(condition?.operator || "").trim() === "required");
    if (!listCondition && !requiredCondition) {
      return field;
    }
    if (requiredCondition && !listCondition) {
      return { ...field, required: true };
    }
    const options = Array.from(new Set(String(listCondition.equalsValue || "")
      .split(",")
      .map((option) => option.trim())
      .filter(Boolean)));
    return { ...field, inputType: "select", options, required: field?.required === true || requiredCondition };
  });
};

export const EMAR_PROTOCOL_RULE_ACTION_OPTIONS = [
  { label: "Give", value: "give" },
  { label: "Hold", value: "hold" },
  { label: "Do Not Give", value: "do_not_give" },
  { label: "Adjust", value: "adjust" },
  { label: "Alert", value: "alert" },
  { label: "Call Provider", value: "call_provider" },
  { label: "Dial 911", value: "call_911" },
  { label: "Calculate Dose", value: "calculate_dose" },
  { label: "Other", value: "custom_action" },
];

export const getEmarProtocolCategoryOption = (value: any) => (
  EMAR_PROTOCOL_CATEGORY_OPTIONS.find((option) => option.value === normalizeKey(value)) || null
);

export const getEmarProtocolEditorCategoryOptions = (currentValue?: any) => {
  const visibleOptions = EMAR_PROTOCOL_CATEGORY_OPTIONS.filter((option) => EMAR_PROTOCOL_EDITOR_VISIBLE_VALUES.has(option.value));
  const currentOption = getEmarProtocolCategoryOption(currentValue);
  if (currentOption && !visibleOptions.some((option) => option.value === currentOption.value)) {
    return [currentOption, ...visibleOptions];
  }
  return visibleOptions;
};

export const getEmarProtocolNumericRangeDefaults = (field: any) => {
  const key = String(field?.key || "").trim();
  switch (key) {
    case "bloodGlucose":
      return { min: 0, max: 600, step: 5 };
    case "systolicBp":
      return { min: 60, max: 240, step: 1 };
    case "diastolicBp":
      return { min: 30, max: 140, step: 1 };
    case "pulse":
      return { min: 30, max: 180, step: 1 };
    case "spo2":
      return { min: 50, max: 100, step: 1 };
    case "painScore":
      return { min: 0, max: 10, step: 1 };
    case "anxietyScore":
      return { min: 0, max: 10, step: 1 };
    case "daysSinceBm":
      return { min: 0, max: 14, step: 1 };
    case "stoolCount":
      return { min: 0, max: 20, step: 1 };
    case "temperature":
      return { min: 95, max: 105, step: 0.1 };
    case "weight":
      return { min: 50, max: 400, step: 1 };
    case "inrValue":
      return { min: 0, max: 10, step: 0.1 };
    case "labValue":
      return { min: 0, max: 100, step: 1 };
    case "dailyIntake":
      return { min: 0, max: 5000, step: 50 };
    case "seizureDuration":
      return { min: 0, max: 30, step: 1 };
    case "respiratoryRate":
      return { min: 0, max: 60, step: 1 };
    default:
      return { min: 0, max: 100, step: 1 };
  }
};

export const buildEmarProtocolRuleAutoLabel = (category: any, conditions: any, index = 0) => {
  const option = getEmarProtocolCategoryOption(category);
  const parameterSchema = Array.isArray(option?.parameterSchema)
    ? (option?.parameterSchema || [])
    : [];
  const parts = parameterSchema
    .map((field: any) => {
      const fieldConditions = conditions?.[field?.key] || {};
      const equalsValue = normalizeText(fieldConditions?.equalsValue);
      const minValue = normalizeText(fieldConditions?.minValue);
      const maxValue = normalizeText(fieldConditions?.maxValue);
      if (equalsValue) {
        return parameterSchema.length === 1
          ? equalsValue
          : `${field?.label || field?.key} ${equalsValue}`;
      }
      if (minValue && maxValue) {
        return parameterSchema.length === 1
          ? `${minValue}-${maxValue}`
          : `${field?.label || field?.key} ${minValue}-${maxValue}`;
      }
      if (minValue) {
        return parameterSchema.length === 1
          ? `>= ${minValue}`
          : `${field?.label || field?.key} >= ${minValue}`;
      }
      if (maxValue) {
        return parameterSchema.length === 1
          ? `<= ${maxValue}`
          : `${field?.label || field?.key} <= ${maxValue}`;
      }
      return "";
    })
    .filter(Boolean);

  return parts.length ? parts.join(", ") : `Rule ${index + 1}`;
};

export const createEmptyProtocolRule = (category: any, index = 0) => {
  const conditions = Object.fromEntries(
    ((getEmarProtocolCategoryOption(category)?.parameterSchema) || []).map((field: any) => [
      field.key,
      {
        operator: ["pain_score", "pain_assessment"].includes(normalizeKey(category)) ? "list" : "equals",
        minValue: "",
        maxValue: "",
        equalsValue: normalizeKey(category) === "pain_score"
          ? "1, 2, 3, 4, 5, 6, 7, 8, 9, 10"
          : normalizeKey(category) === "pain_assessment"
            ? "Mild, Moderate, Severe"
            : "",
      },
    ]),
  );
  return {
    ruleId: `rule_${index + 1}`,
    label: buildEmarProtocolRuleAutoLabel(category, conditions, index),
    action: "give",
    doseValue: "",
    note: "",
    conditions,
  };
};

export const getEmarProtocolSummaries = (medication: any) => {
  if (medication?.protocolEnabled !== true) {
    return [];
  }

  const definitions = Array.isArray(medication?.protocolDefinitions) && medication.protocolDefinitions.length
    ? medication.protocolDefinitions
    : medication?.protocolDefinition
      ? [medication.protocolDefinition]
      : [];

  return definitions
    .map((definition: any) => {
      const category = normalizeText(definition?.category || medication?.protocolCategory);
      const categoryOption = getEmarProtocolCategoryOption(category);
      if (!category && !categoryOption && !definition) {
        return null;
      }
      return {
        category,
        label: normalizeText(definition?.label) || categoryOption?.label || "Protocol Medication",
        guidanceAction: normalizeText(definition?.guidanceAction) || categoryOption?.guidanceAction || "",
        parameterSchema: getEmarProtocolParameterSchema(category, definition?.rules),
        requiresDoseInput: definition?.requiresDoseInput === true || categoryOption?.requiresDoseInput === true,
        instructions: normalizeText(definition?.instructions),
        rules: Array.isArray(definition?.rules) ? definition.rules : [],
      };
    })
    .filter(Boolean);
};

export const getEmarProtocolSummary = (medication: any) => {
  const summaries = getEmarProtocolSummaries(medication);
  if (!summaries.length) {
    return null;
  }
  const labels = Array.from(new Set(summaries.map((summary: any) => normalizeText(summary?.label)).filter(Boolean)));
  const guidanceActions = Array.from(new Set(summaries.map((summary: any) => normalizeText(summary?.guidanceAction)).filter(Boolean)));
  const instructions = summaries
    .map((summary: any) => {
      const summaryInstructions = normalizeText(summary?.instructions);
      if (!summaryInstructions) {
        return "";
      }
      if (summaries.length === 1) {
        return summaryInstructions;
      }
      return `${normalizeText(summary?.label) || "Protocol"}: ${summaryInstructions}`;
    })
    .filter(Boolean)
    .join("\n");

  const parameterSchema = Array.from(
    summaries
      .flatMap((summary: any) => Array.isArray(summary?.parameterSchema) ? summary.parameterSchema : [])
      .reduce((map, field: any) => {
        const key = normalizeText(field?.key);
        if (key && !map.has(key)) {
          map.set(key, field);
        }
        return map;
      }, new Map<string, any>())
      .values(),
  );

  return {
    category: summaries.map((summary: any) => normalizeText(summary?.category)).filter(Boolean).join(","),
    label: labels.length > 1 ? labels.join(" + ") : (labels[0] || "Protocol Medication"),
    guidanceAction: guidanceActions.length > 1 ? guidanceActions.join(" + ") : (guidanceActions[0] || ""),
    parameterSchema,
    requiresDoseInput: summaries.some((summary: any) => summary?.requiresDoseInput === true),
    instructions,
    rules: summaries.flatMap((summary: any) => Array.isArray(summary?.rules) ? summary.rules : []),
    definitions: summaries,
  };
};

export const buildProtocolAssessmentRows = (entry: any, medicationLike?: any) => {
  const protocolAssessment = entry?.protocolAssessment;
  if (!protocolAssessment || typeof protocolAssessment !== "object") {
    return [];
  }

  const storedParameterSchema = Array.isArray(protocolAssessment?.parameterSchema)
    ? protocolAssessment.parameterSchema
    : [];
  const medicationProtocolSchema = storedParameterSchema.length || !medicationLike
    ? []
    : (getEmarProtocolSummary(medicationLike)?.parameterSchema || []);
  const parameterSchema = [...storedParameterSchema, ...medicationProtocolSchema]
    .reduce((fields: any[], field: any) => {
      const key = String(field?.key || "").trim();
      if (key && !fields.some((existingField: any) => String(existingField?.key || "").trim() === key)) {
        fields.push(field);
      }
      return fields;
    }, []);
  const parameterValues = protocolAssessment?.parameterValues && typeof protocolAssessment.parameterValues === "object"
    ? protocolAssessment.parameterValues
    : {};

  const rows = parameterSchema
    .map((field: any) => {
      const key = String(field?.key || "").trim();
      const value = normalizeText(parameterValues[key]);
      if (!key || !value) {
        return null;
      }
      const unit = normalizeText(field?.unit);
      return {
        label: field?.label || key,
        value: unit ? `${value} ${unit}` : value,
      };
    })
    .filter(Boolean) as Array<{ label: string; value: string }>;

  const displayedParameterKeys = new Set(
    parameterSchema.map((field: any) => String(field?.key || "").trim()).filter(Boolean),
  );
  Object.entries(parameterValues).forEach(([key, rawValue]) => {
    const normalizedKey = String(key || "").trim();
    const value = normalizeText(rawValue);
    if (!normalizedKey || !value || displayedParameterKeys.has(normalizedKey)) {
      return;
    }
    rows.push({
      label: formatProtocolParameterLabel(normalizedKey) || normalizedKey,
      value,
    });
  });

  if (normalizeText(protocolAssessment?.doseValue)) {
    rows.push({ label: "Resolved dose", value: normalizeText(protocolAssessment.doseValue) });
  }
  if (normalizeText(protocolAssessment?.decisionNote)) {
    rows.push({ label: "Protocol note", value: normalizeText(protocolAssessment.decisionNote) });
  }
  if (normalizeText(protocolAssessment?.resolution?.recommendedAction)) {
    rows.push({ label: "Recommended action", value: normalizeText(protocolAssessment.resolution.recommendedAction).replaceAll("_", " ") });
  }

  return rows;
};
